import {
    AlertTriangle,
    Briefcase,
    ChevronLeft,
    CircleUserRound,
    LoaderCircle,
    Sparkles,
    Tag,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './AssignIncentivePlanScreen.module.css'

const TYPE_META = {
    SERVICE_COMMISSION: { label: 'Service %', color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { label: 'Retail %', color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'By Category', color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { label: 'Tiered Slabs', color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { label: 'Target Bonus', color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { label: 'Custom', color: '#546e7a', bg: '#eceff1' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const normalizeStaffOptions = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]
    const staffArray = candidates.find(Array.isArray) || []

    return staffArray.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        designationLabel: item?.designationLabel || item?.designation || 'Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
    }))
}

const buildComponentSummary = (component) => {
    if (component?.valueDisplay) return component.valueDisplay

    switch (component?.componentType) {
        case 'SERVICE_COMMISSION':
            return `${component?.rateValue || 0}% of service revenue`
        case 'RETAIL_COMMISSION':
            return `${component?.rateValue || 0}% of product sales`
        case 'ATTENDANCE_BONUS':
            return `₹${Number(component?.bonusValue || 0).toLocaleString('en-IN')} for full attendance`
        case 'TARGET_BONUS':
            return `Hit ₹${Number(component?.targetAmount || 0).toLocaleString('en-IN')} → earn ₹${Number(component?.bonusValue || 0).toLocaleString('en-IN')}`
        default:
            return component?.description || 'Configured as per plan settings'
    }
}

const AssignIncentivePlanScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const {
        staffIdentifier: prefilledStaffId,
        prefillStaffIdentifier,
        staffName: prefilledStaffName,
        excludeTemplateIdentifier,
        templateIdentifier: prefilledTemplateIdentifier,
        templateName: prefilledTemplateName,
        currentIncentiveAssignment,
    } = location.state ?? {}

    const [formData, setFormData] = useState({
        staffIdentifier: prefilledStaffId || prefillStaffIdentifier || '',
        templateIdentifier: prefilledTemplateIdentifier || '',
        effectiveFrom: new Date().toISOString().split('T')[0],
        assignmentRemarks: '',
        revisionReason: '',
    })

    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [staffOptions, setStaffOptions] = useState([])
    const [templateOptions, setTemplateOptions] = useState([])
    const [staffSearch, setStaffSearch] = useState('')
    const [isLoadingStaff, setIsLoadingStaff] = useState(false)
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
    const [isLoadingTemplateDetail, setIsLoadingTemplateDetail] = useState(false)

    const storeIdentifier = activeStore?.storeIdentifier || ''

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }))
        }
    }

    const fetchStaffOptions = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingStaff(true)

            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()

            if (respo?.status === 'success') {
                setStaffOptions(normalizeStaffOptions(respo?.response || {}))
                return
            }

            if (respo?.message === 'tokenExpired') authCtx.logout()
        } catch (error) {
            console.error('getPayrollStaffReadiness error:', error)
        } finally {
            setIsLoadingStaff(false)
        }
    }, [authCtx, storeIdentifier])

    const fetchTemplateOptions = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingTemplates(true)

            const response = await fetch(BASE_URL_API + 'getStoreIncentiveTemplateList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    showArchived: false,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()

            if (respo?.status === 'success') {
                const templates = Array.isArray(respo?.response?.templates)
                    ? respo.response.templates
                    : []

                setTemplateOptions(
                    templates.filter((template) => template.templateIdentifier !== excludeTemplateIdentifier)
                )
                return
            }

            if (respo?.message === 'tokenExpired') authCtx.logout()
        } catch (error) {
            console.error('getStoreIncentiveTemplateList error:', error)
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [authCtx, excludeTemplateIdentifier, storeIdentifier])

    const handleTemplateSelect = useCallback(async (templateId) => {
        setField('templateIdentifier', templateId)
        setSelectedTemplate(null)

        if (!templateId || !authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingTemplateDetail(true)

            const response = await fetch(BASE_URL_API + 'getIncentiveTemplateDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    templateIdentifier: templateId,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()
            if (respo?.status === 'success') {
                setSelectedTemplate(respo.response)
            }
        } catch {
            // Non-blocking preview failure
        } finally {
            setIsLoadingTemplateDetail(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchStaffOptions()
        fetchTemplateOptions()
    }, [fetchStaffOptions, fetchTemplateOptions])

    useEffect(() => {
        if (formData.templateIdentifier) {
            handleTemplateSelect(formData.templateIdentifier)
        }
    }, [formData.templateIdentifier]) // eslint-disable-line react-hooks/exhaustive-deps

    const selectedStaff = useMemo(
        () => staffOptions.find((staff) => staff.staffIdentifier === formData.staffIdentifier) || null,
        [formData.staffIdentifier, staffOptions]
    )

    const filteredStaff = useMemo(() => {
        const q = staffSearch.trim().toLowerCase()
        if (!q) return staffOptions
        return staffOptions.filter((staff) => {
            const hay = `${staff.staffName} ${staff.designationLabel} ${staff.staffCode}`.toLowerCase()
            return hay.includes(q)
        })
    }, [staffOptions, staffSearch])

    const revisionContext = useMemo(() => {
        if (!formData.templateIdentifier) return null

        if (!currentIncentiveAssignment?.templateIdentifier) {
            return { type: 'JOINING', label: 'Initial assignment', color: '#2E7D32', bg: '#e8f5e9' }
        }

        if (currentIncentiveAssignment.templateIdentifier === formData.templateIdentifier) {
            return {
                type: 'INCENTIVE_RATE_CHANGE',
                label: 'Rate refresh — same plan, updated rates',
                color: '#E65100',
                bg: '#fff3e0',
            }
        }

        return {
            type: 'INCENTIVE_STRUCTURE_CHANGE',
            label: 'Plan change — moving to new commission structure',
            color: '#1565C0',
            bg: '#e3f2fd',
        }
    }, [currentIncentiveAssignment, formData.templateIdentifier])

    const previewTemplate = selectedTemplate?.template || null
    const previewComponents = useMemo(
        () => Array.isArray(selectedTemplate?.components?.active) ? selectedTemplate.components.active : [],
        [selectedTemplate]
    )

    const assignIncentivePlan = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        const newErrors = {}
        if (!formData.staffIdentifier) newErrors.staffIdentifier = 'Select a staff member.'
        if (!formData.templateIdentifier) newErrors.templateIdentifier = 'Select a commission plan.'
        if (!formData.effectiveFrom) newErrors.effectiveFrom = 'Select an effective date.'
        if (!formData.assignmentRemarks.trim()) {
            newErrors.assignmentRemarks = 'Provide a brief note for this assignment.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setErrors({})

        try {
            setIsSaving(true)
            console.log(authCtx)

            const response = await fetch(BASE_URL_API + 'assignStaffIncentivePlan', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier: formData.staffIdentifier,
                    templateIdentifier: formData.templateIdentifier,
                    effectiveFrom: formData.effectiveFrom,
                    assignedBy: authCtx?.authIdentifier || '',
                    assignmentRemarks: formData.assignmentRemarks.trim(),
                    revisionReason: formData.revisionReason.trim(),
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const { revisionType, staffName, templateName } = respo.response || {}

                const toastMsg = revisionType === 'JOINING'
                    ? `${staffName} assigned to "${templateName}".`
                    : `${staffName}'s commission plan updated to "${templateName}".`

                toast.success(toastMsg)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if ((respo?.message || '').includes('assignmentRemarks')) {
                        setErrors((prev) => ({ ...prev, assignmentRemarks: respo.message }))
                    } else {
                        toast.error(respo?.message || 'Missing required values.')
                    }
                    break

                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, effectiveFrom: 'Invalid date.' }))
                    break

                case 'VAL_006':
                    setErrors((prev) => ({
                        ...prev,
                        effectiveFrom: `Must be after ${respo?.response?.currentAssignmentEffectiveFrom}.`,
                    }))
                    break

                case 'VAL_007':
                    toast.error('Staff member not found.')
                    break

                case 'COMP_004':
                    setErrors((prev) => ({ ...prev, templateIdentifier: 'Template not found.' }))
                    break

                case 'VAL_008':
                    setErrors((prev) => ({
                        ...prev,
                        templateIdentifier: `"${respo?.response?.templateName}" is archived. Choose an active plan.`,
                    }))
                    break

                case 'SYS_002':
                    toast.error(respo?.message || 'Assignment failed due to rollback.')
                    break

                default:
                    toast.error(respo?.message || 'Failed to assign plan.')
                    break
            }
        } catch (error) {
            console.error('assignStaffIncentivePlan error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Payroll</span>
                    <h1 className={styles.headerTitle}>Assign Commission Plan</h1>
                    {(prefilledStaffName || selectedStaff?.staffName) && (
                        <p className={styles.headerSub}>for {prefilledStaffName || selectedStaff?.staffName}</p>
                    )}
                </div>
            </header>

            <div className={styles.formWrap}>
                {!storeIdentifier && (
                    <section className={styles.alertCard}>
                        <AlertTriangle size={14} />
                        <div>
                            <strong>Select an active store to continue.</strong>
                            <p>Commission assignment is store-specific.</p>
                        </div>
                    </section>
                )}

                {!!formData.staffIdentifier && (
                    <section className={styles.readonlyStaffCard}>
                        <CircleUserRound size={16} />
                        <div>
                            <p className={styles.readonlyLabel}>Staff Member</p>
                            <p className={styles.readonlyValue}>{prefilledStaffName || selectedStaff?.staffName || formData.staffIdentifier}</p>
                        </div>
                    </section>
                )}

                {!formData.staffIdentifier && (
                    <section className={styles.formCard}>
                        <label className={styles.fieldLabel} htmlFor="staffSearch">Staff Member <span className={styles.required}>*</span></label>
                        <input
                            id="staffSearch"
                            className={styles.textInput}
                            type="text"
                            placeholder={isLoadingStaff ? 'Loading staff…' : 'Search by name / code'}
                            value={staffSearch}
                            onChange={(event) => setStaffSearch(event.target.value)}
                        />

                        <div className={styles.pickerList}>
                            {filteredStaff.map((staff) => (
                                <button
                                    type="button"
                                    key={staff.staffIdentifier}
                                    className={`${styles.pickerItem} ${formData.staffIdentifier === staff.staffIdentifier ? styles.pickerItemActive : ''}`}
                                    onClick={() => setField('staffIdentifier', staff.staffIdentifier)}
                                >
                                    <span className={styles.pickerName}>{staff.staffName}</span>
                                    <span className={styles.pickerMeta}>{staff.designationLabel}{staff.staffCode ? ` · ${staff.staffCode}` : ''}</span>
                                </button>
                            ))}
                            {!isLoadingStaff && filteredStaff.length === 0 && (
                                <p className={styles.emptyText}>No staff found.</p>
                            )}
                        </div>
                        {errors.staffIdentifier && <p className={styles.fieldError}>{errors.staffIdentifier}</p>}
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel}>Commission Plan <span className={styles.required}>*</span></label>
                    {isLoadingTemplates && <p className={styles.fieldHint}>Loading commission plans…</p>}
                    <div className={styles.planList}>
                        {templateOptions.map((template) => {
                            const isSelected = formData.templateIdentifier === template.templateIdentifier
                            const typeCodes = Array.isArray(template.componentTypes) ? template.componentTypes : []
                            return (
                                <button
                                    type="button"
                                    key={template.templateIdentifier}
                                    className={`${styles.planCard} ${isSelected ? styles.planCardActive : ''}`}
                                    onClick={() => handleTemplateSelect(template.templateIdentifier)}
                                >
                                    <p className={styles.planName}>{template.templateName}</p>
                                    <div className={styles.typeChipRow}>
                                        {typeCodes.map((type) => {
                                            const meta = TYPE_META[type] || { label: type, color: '#546e7a', bg: '#eceff1' }
                                            return (
                                                <span
                                                    key={`${template.templateIdentifier}-${type}`}
                                                    className={styles.typeChip}
                                                    style={{ color: meta.color, background: meta.bg }}
                                                >
                                                    {meta.label}
                                                </span>
                                            )
                                        })}
                                    </div>
                                </button>
                            )
                        })}

                        {!isLoadingTemplates && templateOptions.length === 0 && (
                            <p className={styles.emptyText}>No active commission plans available.</p>
                        )}
                    </div>
                    {prefilledTemplateIdentifier && (
                        <p className={styles.fieldHint}>Prefilled from: {prefilledTemplateName || 'Commission Plan Detail'}</p>
                    )}
                    {errors.templateIdentifier && <p className={styles.fieldError}>{errors.templateIdentifier}</p>}
                </section>

                {revisionContext && (
                    <section className={styles.contextBanner} style={{ color: revisionContext.color, background: revisionContext.bg }}>
                        <Tag size={13} /> {revisionContext.label}
                    </section>
                )}

                {!!formData.templateIdentifier && (
                    <section className={styles.previewCard}>
                        <div className={styles.previewHeader}>
                            <Briefcase size={14} /> Plan Preview
                            {isLoadingTemplateDetail && <LoaderCircle size={13} className={styles.spin} />}
                        </div>

                        {previewTemplate && (
                            <div className={styles.previewHeadMeta}>
                                <p className={styles.previewTitle}>{previewTemplate.templateName}</p>
                                <span className={styles.versionBadge}>v{previewTemplate.versionNumber || 1}</span>
                            </div>
                        )}

                        {Array.isArray(selectedTemplate?.summary?.componentTypes) && selectedTemplate.summary.componentTypes.length > 0 && (
                            <div className={styles.typeChipRowPreview}>
                                {selectedTemplate.summary.componentTypes.map((type) => {
                                    const meta = TYPE_META[type] || { label: type, color: '#546e7a', bg: '#eceff1' }
                                    return (
                                        <span key={`preview-${type}`} className={styles.typeChip} style={{ color: meta.color, background: meta.bg }}>
                                            <Tag size={11} /> {meta.label}
                                        </span>
                                    )
                                })}
                            </div>
                        )}

                        {!isLoadingTemplateDetail && previewComponents.length === 0 && (
                            <p className={styles.emptyText}>No active commission rules found for this plan.</p>
                        )}

                        {!isLoadingTemplateDetail && previewComponents.length > 0 && (
                            <div className={styles.previewList}>
                                {previewComponents.map((component) => (
                                    <div key={component.componentIdentifier} className={styles.previewRow}>
                                        <span className={styles.previewName}>{component.componentName}</span>
                                        <span className={styles.previewValue}>{buildComponentSummary(component)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="effectiveFrom">Effective From <span className={styles.required}>*</span></label>
                    <input
                        id="effectiveFrom"
                        className={`${styles.textInput} ${errors.effectiveFrom ? styles.inputError : ''}`}
                        type="date"
                        value={formData.effectiveFrom}
                        onChange={(event) => setField('effectiveFrom', event.target.value)}
                    />
                    {errors.effectiveFrom && <p className={styles.fieldError}>{errors.effectiveFrom}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="assignmentRemarks">Assignment Note <span className={styles.required}>*</span></label>
                    <textarea
                        id="assignmentRemarks"
                        className={`${styles.textArea} ${errors.assignmentRemarks ? styles.inputError : ''}`}
                        rows={3}
                        value={formData.assignmentRemarks}
                        onChange={(event) => setField('assignmentRemarks', event.target.value)}
                        placeholder="e.g. Joining plan, Promoted to senior plan, Q2 revision"
                    />
                    {errors.assignmentRemarks && <p className={styles.fieldError}>{errors.assignmentRemarks}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="revisionReason">Detailed Reason (for records)</label>
                    <textarea
                        id="revisionReason"
                        className={styles.textArea}
                        rows={3}
                        value={formData.revisionReason}
                        onChange={(event) => setField('revisionReason', event.target.value)}
                        placeholder="e.g. Performance review, market correction..."
                    />
                </section>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    onClick={assignIncentivePlan}
                    disabled={isSaving || !storeIdentifier}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                        : 'Assign Commission Plan →'}
                </button>
            </div>
        </div>
    )
}

export default AssignIncentivePlanScreen